import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlASIClientV2 } from '@al/asi';
import { AlTrackingMetricEventCategory, AlViewHelperComponent } from '@al/ng-generic-components';
import { AlNavigationService } from '@al/ng-navigation-components';

@Component({
    selector: 'signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {

    productType: string = '';
    productTypeText: string = '';
    awsUrl: string = `https://aws.amazon.com/marketplace/library`;

    amazonMarketplaceToken: string = '';
    form: FormGroup;
 
    products = {
        siemless: 'SIEMless Threat Management',
    };

    errorMessages = {
        'missing_first_name': 'First name is required',
        'missing_last_name': 'Last name is required',
        'missing_user_email': 'Email is required',
        'invalid_email': 'Invalid email address',
        'missing_account_name': 'Company is reqired',
        'invalid_account_name': 'Invalid account name',
        'missing_aws_marketplace_token': 'AWS Marketplace Token is required',
        'invalid_aws_token': 'AWS Marketplace Token is required',
        'aws_token_expired': `When you came from AWS to sign up, the token that they create only lasts for an hour. Unfortunately, that token has expired. Please procced back to our product page on AWS to start the signup process again.`,
        'blacklisted_email': 'Invalid email address',
        'duplicate_email': 'Email is already in use',
        'account_active': "We found an Alert Logic account already associated with this account. So, you don't need to signup again.",
        'invalid_phone_number': 'Invalid phone number',
        'request_entity_too_large': 'An unexpected error has ocurred, plese try again later.'
    }

    step: string = 'signup';
    title: string = 'Finalize Your Subscription';
    descriptions: string[] = [''];
    showMispelledEmail: boolean = false;
    actions: {label: string, event: string}[] | null = null;
    loading: boolean = false;

    @ViewChild(AlViewHelperComponent) viewHelper: AlViewHelperComponent;
    constructor(
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private navigation: AlNavigationService
    ) { }

    ngOnInit(): void {
        this.route.paramMap.subscribe((p) => {
            this.productType = p.get('product');
            this.productTypeText = this.productType in this.products ? this.products[this.productType] : 'Cloud Insight Essentials';
            this.descriptions.push(`Your AWS Marketplace subscription for ${this.productTypeText} is almost complete. Please complete the form,
            and we will send an email containing your login credentials to you.`)
            this.amazonMarketplaceToken = p.get('awsToken');
            this.buildForm();
            this.analytics('loaded');
        });
    }

    buildForm() {
        let defaultForm = {
            first_name: ['', [Validators.required, Validators.pattern("^(?:[A-Za-z]+[ \-.'])*[A-Za-z0-9 \-.']+$")]],
            last_name: ['', [Validators.required, Validators.pattern("^(?:[A-Za-z]+[ \-.'])*[A-Za-z0-9 \-.']+$")]],
            user_email: ['', [Validators.required, Validators.email]],
            account_name: ['', [Validators.required, Validators.pattern(/^(?!alert ?logic)^[.@&]?[a-zA-Z0-9 ]+[ !.@&()]?[ a-zA-Z0-9!().]+/i)]],
            phone_number: ['', [Validators.pattern("^(?=.*[0-9])[- .+ext()0-9]+$")]],
            aws_marketplace_token: [this.amazonMarketplaceToken]
        };
        this.form = this.fb.group({ ... defaultForm });
    }

    async onSubmit() {
        if (this.form.valid) {
            this.loading = true;
            try {
                if (!this.amazonMarketplaceToken) {
                    throw new Error("Invalid Parameter");
                }
                this.analytics('submited');
                await AlASIClientV2.signup(this.form.value);
                this.title = 'Confirm your Subscription';
                this.descriptions = [
                    `We have sent your welcome email to the following email address. Click the activation link in your email to set your password and begin using ${this.productTypeText}.`,
                    `Your account activation link was sent to:`
                ];
                this.showMispelledEmail = true;
                this.step = 'confirm';
                this.analytics('success');
            } catch (error) {
                const msg = error.data && error.data.error ? error.data.error : 'unhandled_error';
                this.manageError(msg);
                this.analytics(`failure.${msg}`);
            } finally {
                this.loading = false;
            }
        }
    }

    manageError(messageCode) {
        if (messageCode in this.errorMessages) {
            switch (messageCode) {
                case 'aws_token_expired':
                    this.title = 'Your token has expired.';
                    this.descriptions = [
                        `When you came from AWS to sign up for ${this.productTypeText}, the token that they create only lasts for an hour. Unfortunately, that token has expired.`,
                        `Please procced back to our product page on AWS to start the signup process again.`
                    ];
                    this.actions = [{ label: 'take me to AWS', event: 'aws' }];
                    this.step = 'awsTokenExpired';
                    break;
                case 'account_active':
                    this.title = 'You already have an account!';
                    this.descriptions = [
                        `We found an Alert Logic account already associated with this account. So, you don't need to signup for ${this.productTypeText} again.`,
                        `Your account login is:`
                    ];
                    this.step = 'alreadyAccount';
                    this.actions = [
                        { label: 'take me to Cloud Insight', event: 'goToALLogin' },
                        { label: 'I forgot my password', event: 'goToResetALPassword' }
                    ];
                    break;
                default:
                    break;
            }
        } else {
            const message = messageCode in this.errorMessages ? this.errorMessages[messageCode] : 'An unexpected error has ocurred, plese try again later.';
            this.viewHelper.notifyError(message, 20000);
        }
    }

    showMispelledEmailInformation(){
        this.title = 'Did you misspell your email address?';
        this.descriptions = [
            `Not a problem!`,
            `Go to "Manage your software subscriptions" in the AWS Marketplace, cancel ${this.productTypeText}, and then sign up for ${this.productTypeText} again using the correct email address.`,
            `You will receive your new activation email shortly.`
        ];
        this.actions = [{ label: 'go to my AWS subscriptions', event: 'aws' }];
        this.showMispelledEmail = false;
        this.step = 'mispellEmail';
        this.analytics('user_email_mismatch');
    }

    executeAction(event: string) {
        switch (event) {
            case 'aws':
                this.analytics('back_to_aws');
                window.location.href = this.awsUrl;
                break;
            case 'goToALLogin':
                this.analytics('account_active.al_login');
                this.navigation.navigate.byNgRoute(['login']);
                break;
            case 'goToResetALPassword':
                this.analytics('account_active.al_reset_pass');
                this.navigation.navigate.byNgRoute(['password', 'reset']);
                break;
            default:
                break;
        }
    }

    analytics(message:string) {
        this.navigation.trackUsageEvent({
            category: AlTrackingMetricEventCategory.MarketplaceAction,
            action: 'Sign Up Action',
            label: message
        });
    }
}
